import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { GenericModal, FeedbackCampaignForm, FeedbackResultsCard, Badge, Button } from '@org/ui';
import type { FeedbackQuestionResult } from '@org/ui';
import { trpc } from '@org/clients/school';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';

export const Route = createFileRoute('/feedback/$campaignId')({
  component: FeedbackDetailRoute,
});

function FeedbackDetailRoute() {
  const { campaignId } = Route.useParams();
  const navigate = useNavigate({ from: '/feedback/$campaignId' });
  const utils = trpc.useUtils();
  const { t } = useTranslation('admin');
  const [tab, setTab] = useState<'edit' | 'results'>('results');

  const { data: campaign, isLoading } = trpc.feedback.getById.useQuery({ campaignId });

  const searchEvents = useCallback(
    async (query: string) => {
      if (query.length < 3) return [];
      return utils.feedback.searchEvents.fetch({ query });
    },
    [utils],
  );

  const searchGroups = useCallback(
    async (query: string) => {
      if (query.length < 3) return [];
      const results = await utils.groups.searchActiveGroups.fetch({ search: query });
      return results.map((g) => ({
        value: g.groupId,
        label: g.description ?? g.groupId,
      }));
    },
    [utils],
  );

  const searchUsers = useCallback(
    async (query: string) => {
      if (query.length < 3) return [];
      const results = await utils.users.searchActiveUsers.fetch({ search: query });
      return results.map((u) => ({
        value: u.userId,
        label: `${u.firstName} ${u.lastName}`,
      }));
    },
    [utils],
  );

  const defaultSelectedGroups = useMemo(
    () =>
      (campaign?.targetGroups ?? []).map((g) => ({
        value: g.groupId,
        label: g.description ?? g.groupId,
      })),
    [campaign],
  );

  const defaultSelectedUsers = useMemo(
    () =>
      (campaign?.targetUsers ?? []).map((u) => ({
        value: u.userId,
        label: `${u.firstName} ${u.lastName}`,
      })),
    [campaign],
  );

  const updateMutation = trpc.feedback.update.useMutation({
    onSuccess: async () => {
      await utils.feedback.list.invalidate();
      await utils.feedback.getById.invalidate({ campaignId });
      toast.success(t('common.toasts.updateSuccess', { entity: 'Campaign' }));
    },
    onError: () => {
      toast.error(t('common.toasts.actionError', { action: 'update', entity: 'campaign' }));
    },
  });

  const deleteMutation = trpc.feedback.delete.useMutation({
    onSuccess: async () => {
      await utils.feedback.list.invalidate();
      toast.success(t('common.toasts.deleteSuccess', { entity: 'Campaign' }));
      handleClose();
    },
    onError: () => {
      toast.error(t('common.toasts.actionError', { action: 'delete', entity: 'campaign' }));
    },
  });

  const handleClose = () => {
    navigate({ to: '/feedback', replace: true });
  };

  // Compute per-question results
  const questionResults = useMemo<FeedbackQuestionResult[]>(() => {
    if (!campaign) return [];

    return campaign.questions.map((q) => {
      const questionType = (q as any).questionType ?? 'rating';
      const submittedResponses = campaign.responses.filter((r) => r.status === 'submitted');
      const answersForQuestion = submittedResponses.flatMap((r) =>
        r.answers.filter((a) => a.questionId === q.questionId),
      );

      if (questionType === 'text') {
        const textResponses = answersForQuestion
          .map((a) => (a as any).textAnswer as string | null)
          .filter((t): t is string => !!t?.trim());

        return {
          questionId: q.questionId,
          questionText: q.questionText,
          questionType: 'text' as const,
          averageRating: null,
          totalResponses: textResponses.length,
          distribution: [0, 0, 0, 0, 0],
          textResponses,
        };
      }

      const distribution = [0, 0, 0, 0, 0];
      let sum = 0;
      for (const a of answersForQuestion) {
        if (a.rating != null) {
          distribution[a.rating - 1]++;
          sum += a.rating;
        }
      }

      return {
        questionId: q.questionId,
        questionText: q.questionText,
        questionType: 'rating' as const,
        averageRating: answersForQuestion.length > 0 ? sum / answersForQuestion.length : 0,
        totalResponses: answersForQuestion.length,
        distribution,
      };
    });
  }, [campaign]);

  const submittedCount = campaign?.responses.filter((r) => r.status === 'submitted').length ?? 0;
  const dismissedCount = campaign?.responses.filter((r) => r.status === 'dismissed').length ?? 0;

  return (
    <GenericModal
      show
      onClose={handleClose}
      title={campaign?.title ?? t('feedback.results.title')}
      size="xl"
      centered
      body={
        isLoading ? (
          <div className="flex h-40 items-center justify-center text-muted-foreground">
            Loading...
          </div>
        ) : !campaign ? (
          <div className="flex h-40 items-center justify-center text-muted-foreground">
            {t('feedback.results.notFound')}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Tab switcher */}
            <div className="flex gap-2 border-b">
              <Button
                type="button"
                variant="ghost"
                className={`h-auto rounded-none px-4 py-2 text-sm font-medium transition hover:bg-transparent ${
                  tab === 'results'
                    ? 'border-b-2 border-primary text-primary hover:text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setTab('results')}
              >
                {t('feedback.results.tabResults')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className={`h-auto rounded-none px-4 py-2 text-sm font-medium transition hover:bg-transparent ${
                  tab === 'edit'
                    ? 'border-b-2 border-primary text-primary hover:text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setTab('edit')}
              >
                {t('feedback.results.tabEdit')}
              </Button>
            </div>

            {tab === 'results' ? (
              <div className="space-y-6">
                {/* Summary stats */}
                <div className="flex gap-4">
                  <Badge variant="outline">
                    {submittedCount} {t('feedback.results.submitted')}
                  </Badge>
                  <Badge variant="secondary">
                    {dismissedCount} {t('feedback.results.dismissed')}
                  </Badge>
                </div>

                {/* Per-question results */}
                {questionResults.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    {t('feedback.results.noResponses')}
                  </p>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {questionResults.map((qr) => (
                      <FeedbackResultsCard
                        key={qr.questionId}
                        question={qr}
                        labels={{
                          responses: t('feedback.results.responsesLabel'),
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <FeedbackCampaignForm
                  defaultValues={{
                    title: campaign.title,
                    description: campaign.description ?? '',
                    campaignType: campaign.campaignType as 'general' | 'event-linked',
                    targetType: campaign.targetType as 'all' | 'groups' | 'users',
                    isMandatory: campaign.isMandatory,
                    eventId: campaign.eventId,
                    questions: campaign.questions.map((q) => ({
                      questionText: q.questionText,
                      questionType: (q.questionType ?? 'rating') as 'rating' | 'text',
                      order: q.order,
                    })),
                  }}
                  onSubmit={(values) =>
                    updateMutation.mutate({ campaignId, ...values })
                  }
                  onClose={handleClose}
                  isSubmitting={updateMutation.isPending}
                  onEventSearch={searchEvents}
                  onSearchGroups={searchGroups}
                  onSearchUsers={searchUsers}
                  defaultSelectedGroups={defaultSelectedGroups}
                  defaultSelectedUsers={defaultSelectedUsers}
                  defaultSelectedEvent={campaign.event ?? undefined}
                  labels={{
                    title: t('feedback.form.title'),
                    description: t('feedback.form.description'),
                    campaignType: t('feedback.form.campaignType'),
                    targetType: t('feedback.form.targetType'),
                    questions: t('feedback.form.questions'),
                    addQuestion: t('feedback.form.addQuestion'),
                    questionPlaceholder: t('feedback.form.questionPlaceholder'),
                    templateHint: t('feedback.form.templateHint'),
                    submit: t('feedback.form.update'),
                    cancel: t('feedback.form.cancel'),
                    recurringExplanation: t('feedback.form.recurringExplanation'),
                  }}
                />
                {/* Delete button */}
                {/* 
                <div className="border-t pt-4">
                  <button
                    type="button"
                    className="text-sm text-destructive hover:underline"
                    onClick={() => {
                      if (window.confirm(t('feedback.form.deleteConfirm'))) {
                        deleteMutation.mutate({ campaignId });
                      }
                    }}
                    disabled={deleteMutation.isPending}
                  >
                    {t('feedback.form.delete')}
                  </button>
                </div> */
                }
              </div>
            )}
          </div>
        )
      }
      footer={<></>}
    />
  );
}
